import { Box } from '@mui/material';
import { FC } from 'react';
import { UnassignedItem } from 'src/entities/dispatch';
import { DisplayMetric } from 'src/pages/ConvenienceDispatches/DisplayMetricToggle';
import { UnassignedCardContent } from 'src/pages/ConvenienceDispatches/UnassignedCardContent';

import { LongHaulDragOverlayContent } from './dragOverlay';

export type LongHaulDragOverlayCardProps = {
  content: LongHaulDragOverlayContent;
  /** 未割当を掴んでいるときのカード。id で引き直した結果（一覧から消えていれば undefined） */
  item?: UnassignedItem;
  displayMetrics: Set<DisplayMetric>;
  customInputFieldNames: string[];
  screenName: string;
};

/**
 * DragOverlay (@dnd-kit/core) の中身。掴んでいるものと、まとめて動かしている件数を描く。
 *
 * 未割当は一覧と同じ中身（`UnassignedCardContent`）を出す。カード配車の `DragOverlayCard`
 * が `DispatchCardView` を出しているのと同じ考え方で、掴む前と掴んだあとで同じものに見える
 * ようにする。
 *
 * Why not カード配車の `DragOverlayCard` をそのまま流用する: あちらは中身を
 * `BoardInteractionContext`（カード配車専用の store）から購読する。この画面は同じ store を
 * 持たないので、Provider ごと持ち込まないと描けない。掴んでいるものは Presenter の state
 * 1 つで足りるため、props で受け取る形にする。
 *
 * Why not 掴んだ時点の値を ref で覚える（DragOverlayCard の lastCardRef）: あちらが覚えるのは
 * store の購読が endDrag でゼロに戻るため。こちらは props で受けるので、ドロップ後の
 * 250ms アニメーション中に dnd-kit が cloneElement で描き続ける要素が、掴んだときの props を
 * そのまま保つ（AnimationManager は children の要素ごと clone する）。
 */
export const LongHaulDragOverlayCard: FC<LongHaulDragOverlayCardProps> = ({
  content, item, displayMetrics, customInputFieldNames, screenName,
}) => {
  const body = content.label != null
    ? (
      // 運行バー。掴んでいる間は 1 行に畳む（バーは日をまたぐと列いっぱいの幅になるので、
      // 元の 3 行をそのまま持ち上げると指の先で読めない大きさになる）
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          px: 1,
          borderRadius: 1.5,
          bgcolor: 'hsl(var(--card))',
          boxShadow: '0 0 0 1px hsl(var(--border)), 0 4px 12px hsl(var(--foreground)/0.16)',
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          color: 'hsl(var(--card-foreground))',
        }}
      >
        {content.label}
      </Box>
    )
    : item && (
      // 未割当カード。枠は一覧のカード（ConvenienceDispatchUnassignedList の
      // DraggableCardItem）と同じ指定を写す
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          borderRadius: 1,
          bgcolor: 'hsl(var(--card))',
          p: 1.5,
          boxShadow: '0 0 0 1px hsl(var(--border)/0.7), 0 2px 4px hsl(var(--foreground)/0.04)',
        }}
      >
        <UnassignedCardContent
          item={item}
          displayMetrics={displayMetrics}
          screenName={screenName}
          customInputFieldNames={customInputFieldNames}
        />
      </Box>
    );

  if (!body) return null;

  return (
    <Box data-testid="long-haul-drag-overlay" sx={{ position: 'relative', height: '100%' }}>
      {body}
      {/* 件数バッジ。サンプル MatchingPresenter/index.tsx:2213-2230 と同じ寸法・色 */}
      {content.count > 1 && (
        <Box
          component="span"
          data-testid="long-haul-drag-overlay-count"
          sx={{
            position: 'absolute',
            right: -8,
            top: -8,
            display: 'flex',
            height: 20,
            width: 20,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            bgcolor: 'hsl(var(--primary))',
            fontSize: 10,
            fontWeight: 700,
            color: 'hsl(var(--primary-foreground))',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          }}
        >
          {content.count}
        </Box>
      )}
    </Box>
  );
};
